import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum, type RumEvent } from "@datadog/browser-rum"

import { AppEnvironment, getAppEnvironment } from "@/lib/app-environment"
import { logger } from "@/lib/logger"

import { redactTelemetryUrl } from "./redactTelemetryUrl"

const log = logger("DatadogRUM")

// Each rule describes a class of events that should be dropped from Datadog.
// The `match` function receives a fully-typed RumEvent and returns true when
// the event should be suppressed.
interface SuppressRule {
  name: string
  reason: string
  match: (event: RumEvent) => boolean
}

// Rules are evaluated in order; first match wins (event is dropped).
export const suppressRules: SuppressRule[] = [
  {
    name: "abort-error",
    reason: "User navigated away mid-request — not actionable (FE-227)",
    match: (event) => {
      if (event.type !== "error") {
        return false
      }
      const { message, type: errType } = event.error
      return (
        errType === "AbortError" ||
        message.includes("The user aborted a request")
      )
    },
  },
  {
    name: "resize-observer-loop",
    reason:
      "Benign browser ResizeObserver notification — not actionable (FE-227)",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("ResizeObserver loop"),
  },
  {
    name: "next-route-cancelled",
    reason: "Next.js navigation cancelled — not an error (FE-227)",
    match: (event) =>
      event.type === "error" && event.error.message.includes("Route Cancelled"),
  },
  {
    name: "posthog-noise",
    reason:
      "PostHog third-party asset/sprite load failures and CSP violations — pure noise (FE-227)",
    match: (event) => {
      if (event.type !== "error") {
        return false
      }
      const { message, source } = event.error
      return (
        message.includes("[Loader.load]") ||
        message.includes("posthog sprites") ||
        (source === "report" && message.toLowerCase().includes("posthog"))
      )
    },
  },
  {
    name: "next-static-props-failure",
    reason:
      "Next.js static props fetch failure — deployment/network noise (FE-227)",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("Failed to load static props"),
  },
  {
    name: "vercel-live-csp-violation",
    reason:
      "CSP violations from vercel.live preview tooling — not production traffic",
    match: (event) =>
      event.type === "error" &&
      event.error.source === "report" &&
      event.error.message.includes("vercel.live"),
  },
  {
    name: "localhost-leak",
    reason:
      "localhost:3000 references leaking to production — dev artifact noise",
    match: (event) => {
      if (event.type !== "error") {
        return false
      }
      const { message, resource } = event.error
      return (
        message.includes("localhost:3000") ||
        (resource?.url.includes("localhost:3000") ?? false)
      )
    },
  },
  {
    name: "chunk-load-failure",
    reason:
      "Webpack chunk load failures — typically stale deployments or network blips, not actionable",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("Failed to load chunk"),
  },
  {
    name: "claude-worktree-noise",
    reason:
      "Errors originating from .claude/worktrees/ — local dev artifact noise, not production",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes(".claude/worktrees/"),
  },
  {
    name: "clerk-missing-publishable-key",
    reason:
      "Clerk SDK complaining about missing publishableKey — config noise in non-auth environments",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("Missing publishableKey"),
  },
  {
    name: "clerk-invitation-revoked",
    reason:
      "Clerk invitation revoked or expired — expected user-facing error, not actionable",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes(
        "This invitation has been revoked and cannot be used anymore",
      ),
  },
  {
    name: "clerk-already-signed-in",
    reason:
      "Clerk reports an already-active session on auth pages — expected redirect state, not actionable",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("You're already signed in."),
  },
  {
    name: "route-change-aborted",
    reason:
      "Next.js routeChange aborted — navigation cancelled mid-flight, not an error",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes("routeChange aborted"),
  },
  {
    name: "next-hard-navigate-same-url",
    reason:
      "Next.js invariant for hard-navigating to the same URL — benign router edge case",
    match: (event) =>
      event.type === "error" &&
      event.error.message.includes(
        "Invariant: attempted to hard navigate to the same URL",
      ),
  },
]

export function shouldSuppressEvent(event: RumEvent): boolean {
  return suppressRules.some((rule) => rule.match(event))
}

function supportsPerformanceEntryType(entryType: string): boolean {
  if (
    typeof window === "undefined" ||
    typeof PerformanceObserver === "undefined"
  ) {
    return false
  }

  return (
    Array.isArray(PerformanceObserver.supportedEntryTypes) &&
    PerformanceObserver.supportedEntryTypes.includes(entryType)
  )
}

const currentAppEnvironment = getAppEnvironment(process.env.NODE_ENV)
const isProduction = currentAppEnvironment === AppEnvironment.Production

// Datadog's `env` tag, not the app environment. The backend, the Lambdas, and
// every dashboard and monitor use `prod`, so the browser must too or its RUM
// and log events fall outside every `env:prod` filter.
const datadogEnv =
  process.env.NEXT_PUBLIC_DATADOG_ENV ??
  (isProduction ? "prod" : AppEnvironment.Development)

if (typeof window !== "undefined") {
  // RUM is opt-out: enabled by default unless explicitly disabled
  const rumEnabled = process.env.NEXT_PUBLIC_DATADOG_RUM_ENABLED !== "false"

  if (
    rumEnabled &&
    process.env.NEXT_PUBLIC_DD_APPLICATION_ID &&
    process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
  ) {
    log.info('Initializing (NEXT_PUBLIC_DATADOG_RUM_ENABLED is not "false")')
    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DD_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: process.env.NEXT_PUBLIC_DD_SITE ?? "datadoghq.com",
      service: "coval",
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      env: datadogEnv,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: supportsPerformanceEntryType("longtask"),
      defaultPrivacyLevel: "mask-user-input",
      // Inject trace headers into same-origin API calls so frontend
      // requests stitch to backend APM traces in Datadog.
      allowedTracingUrls: [window.location.origin],
      // Filter known-noisy, non-actionable errors from Datadog.
      beforeSend: (event) => {
        event.view.url = redactTelemetryUrl(event.view.url)
        if (event.view.referrer) {
          event.view.referrer = redactTelemetryUrl(event.view.referrer)
        }
        if (event.type === "resource") {
          event.resource.url = redactTelemetryUrl(event.resource.url)
        }
        if (event.type === "error" && event.error.resource) {
          event.error.resource.url = redactTelemetryUrl(
            event.error.resource.url,
          )
        }
        return !shouldSuppressEvent(event)
      },
    })
  } else if (process.env.NEXT_PUBLIC_DATADOG_RUM_ENABLED === "false") {
    log.info("Disabled (NEXT_PUBLIC_DATADOG_RUM_ENABLED=false)")
  }

  // Browser logs are opt-out: enabled by default unless explicitly disabled
  const logsEnabled = process.env.NEXT_PUBLIC_DATADOG_LOGS_ENABLED !== "false"

  if (logsEnabled && process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    log.info(
      'Initializing browser logs (NEXT_PUBLIC_DATADOG_LOGS_ENABLED is not "false")',
    )
    datadogLogs.init({
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: process.env.NEXT_PUBLIC_DD_SITE ?? "datadoghq.com",
      service: "coval",
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      env: datadogEnv,
      forwardErrorsToLogs: false,
      beforeSend: (event) => {
        event.view.url = redactTelemetryUrl(event.view.url)
        if (event.view.referrer) {
          event.view.referrer = redactTelemetryUrl(event.view.referrer)
        }
        return true
      },
    })
  } else if (process.env.NEXT_PUBLIC_DATADOG_LOGS_ENABLED === "false") {
    log.info("Browser logs disabled (NEXT_PUBLIC_DATADOG_LOGS_ENABLED=false)")
  }
}

export { datadogRum }
